import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/SEOHead';
import QuoteFormWidget from '../../../components/feature/QuoteFormWidget';

const LandlordBlogPost = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Why Fresno Landlords Need Annual Electrical Safety Checks',
    description: 'Fresno landlords face legal obligations for electrical safety. Learn what California law requires, how electrical inspections protect your rental income, and how to stay compliant.',
    datePublished: '2025-01-30',
    dateModified: '2025-01-30',
    author: { '@type': 'Organization', name: 'Electrician Solutions Fresno' },
    publisher: { '@type': 'Organization', name: 'Electrician Solutions Fresno', url: 'https://electriciansolutionsfresno.com' },
    mainEntityOfPage: 'https://electriciansolutionsfresno.com/blog/landlord-electrical-safety-checks-fresno',
  };

  return (
    <>
      <SEOHead
        title="Landlord Electrical Safety Checks Fresno CA | Legal Requirements | Electrician Solutions"
        description="Fresno landlords face legal obligations for electrical safety. Learn what California law requires, how electrical inspections protect your rental income, and how to stay compliant."
        canonical="/blog/landlord-electrical-safety-checks-fresno"
        keywords="landlord electrical inspection Fresno, rental property electrician Fresno CA, electrical safety landlord Fresno, EICR Fresno"
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        <section className="pt-28 pb-0 bg-[#1a1a1a]">
          <div className="max-w-[900px] mx-auto px-6 pt-6 pb-10">
            <nav className="flex items-center gap-2 text-xs text-gray-500 mb-6">
              <Link to="/" className="hover:text-[#FFD700] transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/blog" className="hover:text-[#FFD700] transition-colors">Blog</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-gray-400">Commercial</span>
            </nav>
            <span className="inline-block bg-[#FFD700] text-[#1a1a1a] text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full mb-4">Commercial</span>
            <h1 className="text-3xl sm:text-5xl font-black text-white mb-5 leading-tight">
              Why Fresno Landlords Need Annual Electrical Safety Checks
            </h1>
            <div className="flex items-center gap-4 text-gray-500 text-sm">
              <span>January 30, 2025</span><span>·</span><span>5 min read</span><span>·</span>
              <span className="text-[#FFD700]">Electrician Solutions Fresno</span>
            </div>
          </div>
        </section>

        <section className="max-w-[900px] mx-auto px-6 -mt-2">
          <div className="rounded-2xl overflow-hidden h-72 sm:h-[440px]">
            <img
              src="https://public.readdy.ai/ai/img_res/6701cd704be434175330f8d99e47b9a2.jpg"
              alt="Landlord electrical safety check Fresno CA"
              className="w-full h-full object-cover"
            />
          </div>
        </section>

        <article className="max-w-[900px] mx-auto px-6 py-12">
          <p className="text-gray-600 text-lg leading-relaxed mb-8">
            As a Fresno landlord, failing to maintain safe electrical systems in your rental property can result in liability claims, fines from the City of Fresno, insurance issues, and serious tenant safety risks. California law requires rental properties to be habitable — and that includes safe, code-compliant electrical systems.
          </p>

          <h2 className="text-2xl font-black text-[#1a1a1a] mt-10 mb-4">What California Law Requires for Rental Property Electrical Systems</h2>
          <p className="text-gray-600 leading-relaxed mb-4">Under California Civil Code Section 1941, landlords must maintain rental properties in a habitable condition. Electrical systems must:</p>
          <ul className="space-y-2 mb-8">
            {[
              'Provide adequate wiring and outlets to meet the needs of normal residential use',
              'Include working smoke detectors in every bedroom and hallway (California Health & Safety Code 13113.7)',
              'Include carbon monoxide detectors on every level if any gas appliances or attached garage are present',
              'Be free from hazards including exposed wiring, overloaded circuits, and faulty panels',
              'Be maintained in good repair at all times',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-gray-600 text-sm p-3 bg-[#f5f5f5] rounded-lg">
                <i className="ri-checkbox-circle-fill text-[#FFD700] text-lg flex-shrink-0 mt-0.5"></i>
                {item}
              </li>
            ))}
          </ul>

          <h2 className="text-2xl font-black text-[#1a1a1a] mt-10 mb-4">5 Reasons Fresno Landlords Should Book Annual Electrical Inspections</h2>
          <div className="space-y-4 mb-8">
            {[
              { title: 'Protect Against Liability', desc: 'If a tenant is injured due to faulty wiring and you haven\'t maintained the property, you could face significant legal liability. An annual inspection creates a documented record of due diligence.' },
              { title: 'Avoid Insurance Claims & Premium Increases', desc: 'Many insurance policies require landlords to maintain electrical systems. An undocumented electrical fault that causes a fire could result in a denied claim.' },
              { title: 'Catch Aging Wiring Before It Becomes a Crisis', desc: 'Many Fresno rental properties — especially in Tower District, Sunnyside, and West Fresno — have aluminum wiring or knob-and-tube wiring from the 1940s-70s that poses serious fire risks.' },
              { title: 'Stay Code-Compliant for Fresno Building Department', desc: 'Fresno Building and Safety conducts periodic inspections of rental units. Non-compliant electrical systems can result in citations and orders to vacate — costing you rental income.' },
              { title: 'Give Tenants Confidence & Reduce Turnover', desc: 'Tenants who feel safe are more likely to renew their lease. A certified electrical safety report is a powerful marketing tool for quality renters.' },
            ].map((item, i) => (
              <div key={i} className="p-5 border-l-4 border-[#FFD700] bg-[#fffbea] rounded-r-xl">
                <h3 className="font-black text-[#1a1a1a] text-sm mb-1">{i + 1}. {item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-black text-[#1a1a1a] mt-10 mb-4">What's Included in Our Fresno Landlord Electrical Inspection</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
            {[
              'Full panel inspection and load assessment',
              'Outlet and switch testing throughout property',
              'GFCI/AFCI protection verification',
              'Smoke detector and CO detector testing',
              'Exterior and garage electrical check',
              'Wiring condition assessment',
              'Written inspection report with photos',
              'Certificate of Electrical Safety issued',
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-[#f5f5f5] rounded-lg text-sm text-gray-600">
                <i className="ri-checkbox-circle-fill text-[#FFD700] text-lg flex-shrink-0"></i>
                {item}
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-black text-[#1a1a1a] mt-10 mb-4">Related Landlord & Property Electrical Services in Fresno</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            {[
              { label: 'Landlord Safety Checks', path: '/services/testing-certification/landlord-electrical-safety-checks', desc: 'Annual electrical certification for Fresno landlords' },
              { label: 'Code Compliance Inspections', path: '/services/testing-certification/code-compliance-inspections', desc: 'Ensure your property meets CA & Fresno codes' },
              { label: 'Wiring Condition Inspections', path: '/services/testing-certification/wiring-condition-inspections', desc: 'Check for aluminum wiring and aging systems' },
              { label: 'Electrical Maintenance Plans', path: '/services/repairs-maintenance/electrical-maintenance-plans', desc: 'Ongoing maintenance for multi-property landlords' },
            ].map(s => (
              <Link key={s.path} to={s.path} className="flex items-start gap-4 p-4 border-2 border-gray-100 hover:border-[#FFD700] rounded-xl transition-all cursor-pointer group">
                <span className="w-8 h-8 flex-shrink-0 flex items-center justify-center mt-0.5">
                  <i className="ri-arrow-right-circle-fill text-[#FFD700] text-xl"></i>
                </span>
                <div>
                  <p className="font-black text-[#1a1a1a] text-sm">{s.label}</p>
                  <p className="text-gray-500 text-xs mt-0.5">{s.desc}</p>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-12 bg-[#1a1a1a] rounded-2xl p-8 sm:p-10">
            <h3 className="text-2xl font-black text-white mb-3">Book a Landlord Electrical Inspection in Fresno</h3>
            <p className="text-gray-400 text-sm mb-6">Protect your property and tenants. We issue a written report and Certificate of Electrical Safety for all Fresno rental properties.</p>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-[#FFD700] text-[#1a1a1a] px-8 py-4 rounded-full font-black text-sm uppercase tracking-wide hover:bg-[#FFC700] transition-all cursor-pointer whitespace-nowrap">
              Book Landlord Inspection <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </article>

        <div className="max-w-[900px] mx-auto px-6 pb-16"><QuoteFormWidget /></div>
        <Footer />
      </div>
    </>
  );
};

export default LandlordBlogPost;
